export type LeadContext = {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  lat: number;
  lng: number;
  formatted?: string;
  available: boolean;
  tower?: string;
  technology?: string;
  checkedAt: string;
};

const STORAGE_KEY = 'airmaxLeadContext';

export function saveLeadContext(ctx: LeadContext): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch (_) {}
}

export function loadLeadContext(): LeadContext | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.address || !parsed?.lat || !parsed?.lng) return null;
    return parsed as LeadContext;
  } catch (_) {
    return null;
  }
}

export function clearLeadContext(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

export function plansPathForCity(city: string): string {
  const c = (city || '').toLowerCase();
  if (c.includes('golden')) return '/internet-golden-valley-az';
  return '/internet-kingman-az';
}

const PLUGIN_API = 'https://airmaxcloud.com/plugin-api/public.php';

export async function geocodeAddress(address: string) {
  const res = await fetch(`${PLUGIN_API}?action=geocodeAddress`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  });
  const data = await res.json();
  if (!res.ok || data?.error) {
    throw new Error(data?.error || 'Could not locate that address');
  }
  return data as {
    ok: boolean;
    lat: number;
    lng: number;
    formatted: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export type PlacePrediction = {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
};

export async function placeAutocomplete(input: string, sessionToken: string) {
  const res = await fetch(`${PLUGIN_API}?action=placeAutocomplete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input, sessionToken }),
  });
  const data = await res.json();
  if (!res.ok || data?.error) {
    throw new Error(data?.error || 'Address suggestions unavailable');
  }
  return (data.predictions || []) as PlacePrediction[];
}

export async function placeDetails(placeId: string, sessionToken: string) {
  const res = await fetch(`${PLUGIN_API}?action=placeDetails`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ placeId, sessionToken }),
  });
  const data = await res.json();
  if (!res.ok || data?.error) {
    throw new Error(data?.error || 'Could not verify that address');
  }
  return data as {
    ok: boolean;
    lat: number;
    lng: number;
    formatted: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

export function newPlacesSessionToken(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export async function checkServiceAtCoords(
  address: string,
  lat: number,
  lng: number,
  opts?: { fullLos?: boolean }
) {
  // Home/plans: fast coverage+LOS block check (no elevation chart).
  // Step 2 on check-service loads fullLos for the terrain chart.
  const fullLos = opts?.fullLos === true;
  const res = await fetch(`${PLUGIN_API}?action=checkPublicCoverage`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address, lat, lng, fullLos: fullLos ? 1 : 0 }),
  });
  const data = await res.json();
  if (!res.ok && data?.error) {
    throw new Error(data.error);
  }
  const available = !!(data.available || data.canConnect) && !data.losBlocked;
  return {
    ...data,
    available,
    canConnect: available,
    losBlocked: !!data.losBlocked,
    losData: data.losData || null,
  } as {
    available?: boolean;
    canConnect?: boolean;
    losBlocked?: boolean;
    tower?: string;
    technology?: string;
    message?: string;
    distanceMiles?: number;
    losSource?: string;
    losData?: {
      towerName: string;
      distanceMiles: number;
      blocked: boolean;
      obstructions: number;
      towerHeightFt?: number;
      clientHeightFt?: number;
      elevationProfile: Array<{
        distanceMiles: number;
        elevation: number;
        losElevation: number | null;
        fresnelRadius?: number;
        isObstructed?: boolean;
        isTower?: boolean;
        isClient?: boolean;
      }>;
      source?: string;
    } | null;
  };
}

export function buildCheckServiceUrl(opts: {
  plan?: { id?: number; name: string; price: number; speed: string };
  lead?: LeadContext | null;
}): string {
  const params = new URLSearchParams();
  const { plan, lead } = opts;
  if (plan?.name) params.set('package', plan.name);
  if (plan?.price != null) params.set('price', String(plan.price));
  if (plan?.speed) params.set('speed', plan.speed);
  if (plan?.id != null) params.set('planId', String(plan.id));
  if (lead?.address) params.set('address', lead.address);
  if (lead?.city) params.set('city', lead.city);
  if (lead?.state) params.set('state', lead.state);
  if (lead?.zipCode) params.set('zip', lead.zipCode);
  if (lead?.lat) params.set('lat', String(lead.lat));
  if (lead?.lng) params.set('lng', String(lead.lng));
  // After home/plans check we already know coverage — jump to contact
  if (lead?.address && lead?.lat && lead?.lng) {
    params.set('available', lead.available ? '1' : '0');
    params.set('skipToContact', '1');
    params.set('verified', '1');
  }
  const qs = params.toString();
  return `/check-service${qs ? `?${qs}` : ''}`;
}
